<template>
  <div class="flex flex-col h-full content-start space-y-5 grid gap-2">
    <div class="mb-8 pb-8">
      <Icon @click="goBack" icon="ant-design:arrow-left-outlined" class="text-xl cursor-pointer" />
      <h5 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Transition</h5>
      <p>
        A transition is a word or phrase that connects consecutive sentences or paragraphs.
        Effective transitions can clarify the logical flow of your ideas and thus strengthen your
        argument, so that there are no abrupt breaks between ideas.
      </p>
      <p class="text-primary-main">
        <i
          >The proposed solution to the problem did not work. Therefore, we attempted a second
          solution. However, this solution was also unsuccessful.</i
        >
      </p>
      <h5 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Sentence connections</h5>
      <p>
        We use transition words to provide a coherent, clear flow of ideas. You have to show how
        each idea relates to the other preceding and subsequent ideas that are discussed. To achieve
        this cohesion, transition words commonly appear at the start of a new sentence or clause
        (followed by a comma), serving to express how this clause relates to the previous one.
        Transition words can also appear in the middle of a clause. It is important to place
        transition words correctly to convey the meaning you intend. The goal you should keep in
        mind is to have your paper “flow” (continuity and progression of ideas), such that the
        reader can understand why a new idea is being introduced and what role it serves in the
        text. For example, our new idea may serve as a specific example illustrating the previous
        idea, or perhaps builds on or presents an opposing viewpoint to something that was discussed
        earlier. There are different ways to connect sentences.
      </p>
      <ol class="list-decimal grid gap-4 flex-1 p-5">
        <li>
          To overlap, meaning to repeat something from the previous sentence:
          <p class="text-primary-main">
            <i
              >The pattern of inflammation during an asthma attack is different from that seen in
              stable asthma. In stable asthma the total number of inflammatory cells does not
              increase.
            </i>
          </p><br />
          <p class="text-primary-main">
            <i
              >One way to toughen polymers is to incorporate a layer of rubber particles. As a
              result, there has been extensive research regarding the rubber modification of PLA.
            </i>
          </p>
        </li>
        <li>
          Use a pronoun (it, they) or pro-form (this method, these systems) to connect the sentences
          together:
          <p class="text-primary-main">
            <i
              >Many researchers have suggested ways of reducing cost without affecting the quality
              of the image. These methods rely on data structures built during a preprocessing
              step.</i
            >
          </p><br />
          <p class="text-primary-main">
            <i
              >On the basis of these criteria, the software then describes the preparation of a set
              of polymer blends using PLA and a hydrocarbon rubber (PI). This combination of two
              mechanistically distinct polymerizations formed a novel copolymer in which the
              incorporation of PI significantly increased flexibility.</i
            >
          </p>
        </li>
        <li>
          Do not finish your sentence, but join it with the next sentence using a semicolon or a
          relative clause (a ‘which’ clause). Joining sentences with a semicolon works well when two
          sentences are very closely related and one of them is quite short:
          <p class="text-primary-main">
            <i
              >The procedure for testing if components are operationally safe usually takes many
              hours; this means that tests are rarely repeated.</i
            >
          </p><br />
          <p class="text-primary-main">
            <i
              >It has received much attention over the past few decades due to its biodegradable
              properties, which offer important economic benefits.</i
            >
          </p>
        </li>
        <li>
          Use a signaling sentence connector to indicate the relationship between one sentence and
          the next. There are several types of transitions, and each category leads readers to make
          certain connections or assumptions. We can divide all transitions into four basic
          categories:
          <br />
          <ul class="list-disc grid gap-4 flex-1 p-5">
            <li>
              <b>Additive transitions</b> signal to the reader that you are adding or referencing
              information
            </li>
            <li>
              <b>Adversative transitions</b> indicate conflict or disagreement between pieces of
              information
            </li>
            <li>
              <b>Causal transitions</b> transitions point to consequences and show cause-and-effect
              relationships
            </li>
            <li>
              <b>Sequential transitions</b> clarify order and sequence of information and the
              overall structure of the paper
            </li>
          </ul>
        </li>
      </ol>

      <h4 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Additive transition</h4>
      <p>
        Additive transition words: <b>a)</b> introduce new information (between both sentences and
        paragraphs); <b>b)</b> provide examples; <b>c)</b> or refer to something that was just
        mentioned; <b>d)</b> to expand upon and idea; <b>e)</b> add similar situation;
        <b>f)</b> compare with; <b>g)</b> or identify certain information as important; or
        <b>h)</b> clarify the preceding text.
      </p>
      <table>
        <tr>
          <th><b>Function</b></th>
          <th><b>Example sentence</b></th>
          <th><b>Transition words and phrases</b></th>
        </tr>
        <tr>
          <td><b>Addition</b></td>
          <td>
            We found that the mixture was effective. <b>Moreover</b>, it appeared to have additional
            effects we had not predicted.
          </td>
          <td>
            indeed, furthermore, moreover, additionally, and, also, both x and y, not only x but
            also y, besides x, in fact
          </td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Introduction</b></td>
          <td>
            Several researchers have previously explored this topic. <b>For instance</b>, Smith
            (2014) examined the effects of ...
          </td>
          <td>
            such as, like, particularly, including, as an illustration, for example, for instance,
            in particular, to illustrate, especially, notably
          </td>
        </tr>
        <tr>
          <td><b>Reference</b></td>
          <td>
            The solution showed a high degree of absorption. <b>Considering this result</b>, it is
            reasonable to conclude that ...
          </td>
          <td>
            considering x, regarding x, in regard to x, as for x, concerning x, the fact that x, on
            the subject of x
          </td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Similarity</b></td>
          <td>
            It was not possible to establish a correlation between these variables.
            <b>Similarly</b>, the connection between x and y remains unclear ...
          </td>
          <td>similarly, in the same way, by the same token, in like manner, equally, likewise</td>
        </tr>
        <tr>
          <td><b>Clarification</b></td>
          <td>
            The patient suffered several side effects, <b>namely</b> increased appetite, decreased
            libido, and disordered sleep.
          </td>
          <td>that is (to say), namely, specifically, more precisely, in other words</td>
        </tr>
      </table>

      <h4 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Adversative transition</h4>
      <p>
        Adversative transition words always signal a contrast of some kind. These terms and phrases:
        <b>a)</b> distinguish facts, arguments, and other information, whether by introducing
        information that disagrees or contrasts with the preceding text; <b>b)</b> by conceding
        points or making counterarguments; <b>c)</b> by dismissing the importance of a fact or
        argument; <b>d)</b> or replacing and suggesting alternatives.
      </p>
      <table>
        <tr>
          <th><b>Function</b></th>
          <th><b>Example sentence</b></th>
          <th><b>Transition words and phrases</b></th>
        </tr>
        <tr>
          <td><b>Conflict</b></td>
          <td>
            The novel does deal with the theme of family. <b>However</b>, its central theme is more
            broadly political ...
          </td>
          <td>
            but, however, although, though, equally, by way of contrast, while, on the other hand,
            (and) yet, whereas, in contrast, (when) in fact, conversely, whereas
          </td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Concession</b></td>
          <td>
            Jones (2011) argues that the novel reflects Russian politics of the time.
            <b>Although</b> this is correct, other aspects of the text must also be considered.
          </td>
          <td>
            even so, nonetheless, nevertheless, even though, on the other hand, admittedly, despite
            x, notwithstanding x, (and) still, although, in spite of x, regardless (of x), (and)
            yet, though, granted x
          </td>
        </tr>
        <tr>
          <td><b>Dismissal</b></td>
          <td>
            It remains unclear which of these hypotheses is correct. <b>In any case</b>, it can be
            inferred that ...
          </td>
          <td>
            regardless, either way, whatever the case, in any/either event, in any/either case, at
            any rate, all the same
          </td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Emphasis</b></td>
          <td>
            The chemical is generally thought to have corrosive properties. <b>Indeed</b>, several
            studies have supported this hypothesis.
          </td>
          <td>above all, indeed, more/most importantly</td>
        </tr>
        <tr>
          <td><b>Replacement</b></td>
          <td>
            The character of Godfrey is often viewed as selfish, <b>or at least</b> self-absorbed.
          </td>
          <td>(or) at least, (or) rather, instead, or (perhaps) even, if not</td>
        </tr>
      </table>

      <h4 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Causal transition</h4>
      <p>
        Causal transition words are used to signal the reasons, conditions, purposes, circumstances,
        describe cause and effect relationships. These transitions often come after an important
        point in the paper has been established or to explore hypothetical relationships or
        circumstances.
      </p>
      <table>
        <tr>
          <th><b>Function</b></th>
          <th><b>Example sentence</b></th>
          <th><b>Transition words and phrases</b></th>
        </tr>
        <tr>
          <td><b>Consequence</b></td>
          <td>
            <b>Because</b> Hitler failed to respond to the British ultimatum, France and the UK
            declared war on Germany.
          </td>
          <td>
            therefore, because (of x), as a result (of x), for this reason, in view of x, as, owing
            to x, due to (the fact that), since, consequently, in consequence, as a consequence,
            hence, thus, so (that), accordingly, so much (so) that, under the/such circumstances, if
            so
          </td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Condition</b></td>
          <td>
            We qualified survey responses as positive <b>only if</b> the participant selected
            “agree” or “strongly agree.” <b>Otherwise</b>, results were recorded as negative.
          </td>
          <td>
            (even/only) if/when, on (the) condition that, in the case that, granted (that),
            provided/providing that, in case, in the event that, as/so long as, unless, given that,
            being that, inasmuch/insofar as, in that case, in (all) other cases, if so/not,
            otherwise
          </td>
        </tr>
        <tr>
          <td><b>Purpose</b></td>
          <td>
            We used accurate recording equipment <b>so that</b> our results would be as precise as
            possible.
          </td>
          <td>
            to, in order to/that, for the purpose of, in the hope that, so that, to the end that,
            lest, with this in mind, so as to, so that, to ensure (that)
          </td>
        </tr>
      </table>

      <h4 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Sequential transition</h4>
      <p>
        Sequential transition words organize your paper in a sequence, whether it is the order in
        which events occurred chronologically or the order you are presenting them in your text
        (numerical). This shows: <b>a)</b> continuation in thought or action; <b>b)</b> by referring
        to previously-mentioned information; <b>c)</b> by concluding and summing up your paper.
        Sequential transitions are essential to creating structure and helping the reader understand
        the logical development through your paper’s methods, results, and analysis.
      </p>
      <table>
        <tr>
          <th><b>Function</b></th>
          <th><b>Example sentence</b></th>
          <th><b>Transition words and phrases</b></th>
        </tr>
        <tr>
          <td><b>Enumeration</b></td>
          <td>
            This has historically had several consequences: <b>First</b>, the conflict is not given
            the weight of other conflicts in historical narratives. <b>Second</b>, its causes are
            inadequately understood. <b>Third</b>, ...
          </td>
          <td>first, second, third ...</td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Initiation</b></td>
          <td><b>To begin with</b>, I want to consider the role played by women in this period.</td>
          <td>in the first place, initially, first of all, to begin with, at first</td>
        </tr>
        <tr>
          <td><b>Continuation</b></td>
          <td>
            <b>Subsequently</b>, I discuss the way in which the country’s various ethnic minorities
            were affected by the conflict.
          </td>
          <td>subsequently, previously, eventually, next, before x, afterwards, after x, then</td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Conclusion</b></td>
          <td><b>Finally</b>, I consider these two themes in combination.</td>
          <td>
            to conclude (with), as a final point, eventually, at last, last but not least, finally,
            lastly
          </td>
        </tr>
        <tr>
          <td><b>Resumption</b></td>
          <td><b>To return to</b> my main argument, it is clear that ...</td>
          <td>to return/returning to x, to resume, at any rate</td>
        </tr>
        <tr style="background-color: #f8f7f5">
          <td><b>Summation</b></td>
          <td>
            Patel (2015) comes to a similar conclusion. <b>In summary</b>, the four studies
            considered here suggest a consensus that the solution is effective.
          </td>
          <td>
            as previously stated/mentioned, in summary, as I have argued, overall, as has been
            mentioned, to summarize, briefly, given these points, in view of x, as has been noted,
            in conclusion, in sum, altogether, in short
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { Icon } from '@iconify/vue'
import { Vue, Options } from 'vue-class-component';

@Options({
  components: {
    Icon
  }
})
export default class TransitionLearnMore extends Vue {
  public goBack() {
    this.$emit('closeLearnMore')
  }
}
</script>

<style>
table {
  font-family: arial, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}
</style>
